import { ReactNode, useState } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Star, Lock, Sparkles, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface UpgradePromptProps {
  feature: string;
  children: ReactNode;
  requiredTier?: 'premium_economy' | 'first_class';
  title?: string;
  description?: string;
  benefits?: string[];
}

export function UpgradePrompt({ 
  feature, 
  children, 
  requiredTier = 'premium_economy',
  title,
  description,
  benefits = []
}: UpgradePromptProps) {
  const { hasFeature, subscription } = useSubscription();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  if (hasFeature(feature)) {
    return <>{children}</>;
  }

  const tierConfig = {
    premium_economy: {
      name: 'Premium Economy',
      icon: Star,
      price: '€6.99/month',
      color: 'from-cyan-500 to-teal-600',
      textColor: 'text-cyan-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
      defaultBenefits: [
        'Unlimited searches',
        'AI-powered packing lists',
        'Group expense sharing',
        'Local destination tips',
        'Early access to features'
      ]
    },
    first_class: {
      name: 'First Class',
      icon: Crown,
      price: '€11.99/month',
      color: 'from-yellow-500 to-yellow-600',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50 dark:bg-yellow-950/30',
      defaultBenefits: [
        'Everything in Premium Economy',
        'Multi-city packing lists',
        'Group packing progress',
        'Advanced travel insights',
        'Priority support'
      ]
    }
  };

  const config = tierConfig[requiredTier];
  const Icon = config.icon;
  const displayBenefits = benefits.length > 0 ? benefits : config.defaultBenefits;

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          tier: requiredTier,
          return_url: window.location.href
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to start upgrade process. Please try again.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  return (
    <Card className="border overflow-hidden">
      <CardHeader className="text-center pb-4">
        <div className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${config.color}`}>
          <Lock className="h-5 w-5 text-white" />
        </div>
        
        <CardTitle className="text-lg">
          {title || `Upgrade to ${config.name}`}
        </CardTitle>
        <CardDescription>
          {description || `This feature requires ${config.name} subscription`}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {displayBenefits.map((benefit, index) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <Icon className={`h-4 w-4 mt-0.5 ${config.textColor} flex-shrink-0`} />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        <Button 
          className={`w-full bg-gradient-to-r ${config.color} text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/25`}
          onClick={handleUpgrade}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <LoadingSpinner size="sm" />
              <span>Processing...</span>
            </div>
          ) : (
            `Upgrade for ${config.price}`
          )}
        </Button>
      </CardContent>
    </Card>
  );
}